import {t} from '../../i18n/i18n.js';
import {hasStoredContent} from '../../core/page/form.core.js';
import {SETTING_FIELD_KEYS} from '../../core/vocabulary.js';

const SETTING_KEYS = new Set(SETTING_FIELD_KEYS);

/**
 * Keeps URL/search settings together while preserving the direct field groups
 * that the section editor and layout cards use. Composition pages already
 * have their own page-content heading.
 * @param {HTMLElement} container
 */
export function arrangeFieldSections(container) {
    const directGroups = [...container.children].filter((node) => node.classList.contains('field-group'));
    const settings = directGroups.filter((group) => SETTING_KEYS.has(group.dataset.fieldKey));
    const content = directGroups.filter((group) => !SETTING_KEYS.has(group.dataset.fieldKey));
    const composition = container.querySelector(':scope > .composition-panel');
    if (settings.length === 0 || (content.length === 0 && !composition)) return;

    const contentAnchor = content[0] ?? composition;
    for (const group of settings) container.insertBefore(group, contentAnchor);

    const settingsHeading = document.createElement('h3');
    settingsHeading.className = 'form-section-heading';
    settingsHeading.dataset.i18n = 'pageForm.settingsTitle';
    settingsHeading.textContent = t('pageForm.settingsTitle');
    container.insertBefore(settingsHeading, settings[0]);

    if (content.length > 0 && !composition) {
        const contentHeading = document.createElement('h3');
        contentHeading.className = 'form-section-heading form-section-heading--content';
        contentHeading.dataset.i18n = 'pageForm.compositionTitle';
        contentHeading.textContent = t('pageForm.compositionTitle');
        container.insertBefore(contentHeading, contentAnchor);
    }
}

/**
 * Template authors can place fields that make up one visible page block in a
 * single editor card. This only moves existing controls, so their values,
 * upload state and form submission names are preserved.
 * @param {HTMLElement} container
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 */
export function renderEditorGroups(container, fields) {
    const labels = new Map(fields.map((field) => [field.key, field.editorGroup?.trim()]));
    const schemaOrder = new Map(fields.map((field, index) => [field.key, index]));
    const cards = new Map();

    for (const fieldGroup of [...container.children]) {
        if (!fieldGroup.classList.contains('field-group')) continue;
        const key = fieldGroup.dataset.fieldKey;
        const label = labels.get(key);
        if (!label || SETTING_KEYS.has(key)) continue;

        let card = cards.get(label);
        if (!card) {
            card = document.createElement('details');
            card.className = 'editor-group';
            card.setAttribute('aria-label', label);

            const heading = document.createElement('summary');
            heading.className = 'editor-group-title';
            heading.textContent = label;
            const body = document.createElement('div');
            body.className = 'editor-group-fields';
            card.append(heading, body);
            fieldGroup.insertAdjacentElement('beforebegin', card);
            cards.set(label, card);
        }
        card.querySelector('.editor-group-fields').appendChild(fieldGroup);
    }
    for (const card of cards.values()) {
        const body = card.querySelector('.editor-group-fields');
        [...body.children]
            .sort((a, b) => schemaOrder.get(a.dataset.fieldKey) - schemaOrder.get(b.dataset.fieldKey))
            .forEach((fieldGroup) => body.appendChild(fieldGroup));
    }
    if (cards.size === 0) return;

    // Explicit editor groups describe the page sequence. Sort the surrounding
    // ungrouped content with them by schema order, so metadata in the document
    // head cannot pull a later page block ahead of the hero.
    const content = [...container.children].filter((node) =>
        node.classList.contains('editor-group') ||
        (node.classList.contains('field-group') && !SETTING_KEYS.has(node.dataset.fieldKey))
    );
    const firstIndex = (node) => node.classList.contains('editor-group')
        ? Math.min(...[...node.querySelectorAll('.field-group')].map((field) => schemaOrder.get(field.dataset.fieldKey)))
        : schemaOrder.get(node.dataset.fieldKey);
    const anchor = container.querySelector(':scope > .composition-panel, :scope > .layout-context-card');
    content.sort((a, b) => firstIndex(a) - firstIndex(b))
        .forEach((node) => container.insertBefore(node, anchor));
}

/**
 * Moves a layout-context card into the editor group of its toggle field.
 * @param {HTMLElement} container
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {Map<string, string>} toggleFieldsByContext
 */
export function placeLayoutContextCardsInEditorGroups(container, fields, toggleFieldsByContext) {
    const groupByFieldKey = new Map(fields.map((field) => [field.key, field.editorGroup?.trim()]));
    for (const [context, toggleKey] of toggleFieldsByContext) {
        const groupLabel = groupByFieldKey.get(toggleKey);
        if (!groupLabel) continue;
        const group = [...container.querySelectorAll(':scope > .editor-group')]
            .find((item) => item.getAttribute('aria-label') === groupLabel);
        const card = [...container.querySelectorAll(':scope > .layout-context-card')]
            .find((item) => item.dataset.context === context);
        if (group && card) group.querySelector('.editor-group-fields').appendChild(card);
    }
}

/**
 * Opens the editor groups that stored page data fills and closes the rest.
 * @param {ParentNode} root
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {Object<string, *>} data
 */
export function syncEditorGroupExpansion(root, fields, data) {
    const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
    for (const group of root.querySelectorAll('.editor-group')) {
        group.open = [...group.querySelectorAll('.field-group')].some((fieldGroup) =>
            hasStoredContent(fieldsByKey.get(fieldGroup.dataset.fieldKey), fieldGroup.dataset.fieldKey, data));
    }
}
